// src/screens/VoyantCapture.tsx
// ─────────────────────────────────────────────────────────────────────────────
// PRE-SESSION SETUP — adviser fills before client arrives.
// Left: tax year, freedom age, session type.
// Right: Voyant do-nothing capture (lockable).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { ClientData, VoyantCapture as VoyantCaptureType } from '../types/ClientData';
import { ALL_TAX_YEARS, TaxYear, getTaxYearForDate } from '../taxConfig';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';
import { TallySubmission, mapTallyToClientData } from '../utils/tallyImport';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function VoyantCaptureScreen({ data, onUpdate }: Props) {
  const [checklist, setChecklist] = useState({
    voyantBuilt: false,
    salaryConfirmed: false,
    balanceSheetReady: false,
    sessionDateSet: false,
  });
  const [tallyJson, setTallyJson] = useState('');
  const [tallyError, setTallyError] = useState('');
  const [tallySuccess, setTallySuccess] = useState('');

  const autoTaxYear = getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const activeTaxYear = data.taxYearOverride ?? autoTaxYear;

  const voyant = data.voyantDoNothing;
  const isLocked = voyant.capturedDate !== '';

  // Create hook instances for each currency field
  const potInput = useCommaInput(voyant.potAtRetirement ?? 0, v => handleVoyantField('potAtRetirement', v));
  const spendingInput = useCommaInput(voyant.annualIncomeAchievable ?? 0, v => handleVoyantField('annualIncomeAchievable', v));
  const lumpSumInput = useCommaInput(voyant.voyantLumpSumNeeded ?? 0, v => handleVoyantField('voyantLumpSumNeeded', v));
  const annualSavingsInput = useCommaInput(voyant.voyantAnnualSavingsNeeded ?? 0, v => handleVoyantField('voyantAnnualSavingsNeeded', v));
  const assetsInput = useCommaInput(voyant.voyantAssetsAtTarget ?? 0, v => handleVoyantField('voyantAssetsAtTarget', v));
  const liabilitiesInput = useCommaInput(voyant.voyantLiabilitiesAtTarget ?? 0, v => handleVoyantField('voyantLiabilitiesAtTarget', v));
  const netWorthInput = useCommaInput(voyant.voyantNetWorthAtTarget ?? 0, v => handleVoyantField('voyantNetWorthAtTarget', v));

  const handleVoyantField = (field: keyof VoyantCaptureType, value: number | null) => {
    onUpdate({
      voyantDoNothing: { ...data.voyantDoNothing, [field]: value },
    });
  };

  const handleLock = () => {
    onUpdate({
      voyantDoNothing: {
        ...data.voyantDoNothing,
        capturedDate: new Date().toISOString(),
      },
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ma-navy">Pre-Session Setup</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Complete before the client arrives. This sets the context for the entire session.
          </p>
        </div>
        <div className="bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full">
          Adviser only
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT — Session setup */}
        <div className="space-y-5">

          {/* Tax year selector */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">Tax Year</h3>
            <p className="text-xs text-gray-400 mb-4">Planning for:</p>
            <div className="flex gap-3">
              {ALL_TAX_YEARS.map(yr => (
                <button
                  key={yr}
                  onClick={() => onUpdate({ taxYearOverride: yr === autoTaxYear ? undefined : yr })}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 text-center transition-all ${
                    activeTaxYear === yr
                      ? 'border-ma-navy bg-ma-navy/5 text-ma-navy font-semibold'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-bold">{yr}</div>
                  {yr === autoTaxYear && (
                    <div className="text-xs text-gray-400 mt-1">Auto-detected</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Target freedom age */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-1">Target Freedom Age</h3>
            <p className="text-xs text-gray-400 mb-4">The most important single input. What age does the client want financial freedom?</p>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20 focus-within:border-ma-navy/40">
              <span className="text-gray-400 text-sm mr-2">Age</span>
              <input
                type="number"
                min="40"
                max="75"
                value={data.targetFreedomAge || ''}
                onChange={e => onUpdate({ targetFreedomAge: parseInt(e.target.value) || 55 })}
                className="flex-1 bg-transparent text-2xl font-bold text-ma-navy outline-none text-center"
              />
            </div>
            {data.clientAge > 0 && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                {data.targetFreedomAge - data.clientAge} years from now (client is {data.clientAge})
              </p>
            )}
          </div>

          {/* Session type */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">Session Type</h3>
            <div className="space-y-2">
              {([
                { value: 'new', label: 'New Client', sub: 'First session — full programme' },
                { value: 's2', label: 'Returning (Session 2)', sub: 'Business engine — employer pension, GapHQ' },
                { value: 's3', label: 'Returning (Session 3)', sub: 'Voyant precision — life design, stress testing' },
                { value: 'annual', label: 'Annual Review', sub: 'Progress check — update numbers, review levers' },
              ] as const).map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  data.sessionType === opt.value
                    ? 'border-ma-navy bg-ma-navy/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio" name="sessionType" value={opt.value}
                    checked={data.sessionType === opt.value}
                    onChange={() => onUpdate({ sessionType: opt.value })}
                    className="mt-0.5 accent-ma-navy"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{opt.sub}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Tally intake import */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-3">Import from intake form</h3>
            <textarea
              value={tallyJson}
              onChange={e => { setTallyJson(e.target.value); setTallyError(''); setTallySuccess(''); }}
              rows={4}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20 resize-none font-mono"
              placeholder="Paste Tally JSON response here..."
            />
            {tallyError && (
              <p className="text-xs text-red-500 mt-1">{tallyError}</p>
            )}
            {tallySuccess && (
              <p className="text-xs text-green-600 mt-1">{tallySuccess}</p>
            )}
            <button
              onClick={() => {
                try {
                  const parsed = JSON.parse(tallyJson) as TallySubmission;
                  if (!parsed.fields || !Array.isArray(parsed.fields)) {
                    setTallyError('Invalid format — paste the Tally webhook payload.');
                    return;
                  }
                  const result = mapTallyToClientData(parsed);
                  const fieldCount = (result as any)._tallyFieldCount || 0;
                  delete (result as any)._tallyFieldCount;
                  // Merge — only overwrite non-zero/non-empty fields
                  const cleaned: Partial<ClientData> = {};
                  for (const [k, v] of Object.entries(result)) {
                    if (v !== undefined && v !== 0 && v !== '') {
                      (cleaned as any)[k] = v;
                    }
                  }
                  onUpdate(cleaned);
                  setTallySuccess(`Client data imported ✓ — ${fieldCount} fields populated`);
                  setTallyJson('');
                  setTallyError('');
                } catch {
                  setTallyError('Invalid format — paste the Tally webhook payload.');
                }
              }}
              disabled={!tallyJson.trim()}
              className="mt-3 w-full bg-ma-navy text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              📥 Import client data
            </button>
          </div>

          {/* Pre-session checklist */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">Pre-Session Checklist</h3>
            <div className="space-y-3">
              {[
                { key: 'voyantBuilt' as const, label: 'Voyant do-nothing scenario built' },
                { key: 'salaryConfirmed' as const, label: 'Salary and dividend figures confirmed from Xero' },
                { key: 'balanceSheetReady' as const, label: 'Balance sheet figures ready' },
                { key: 'sessionDateSet' as const, label: 'Session date set above' },
              ].map(item => (
                <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist[item.key]}
                    onChange={e => setChecklist(prev => ({ ...prev, [item.key]: e.target.checked }))}
                    className="w-4 h-4 rounded accent-ma-navy"
                  />
                  <span className={`text-sm ${checklist[item.key] ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Voyant do-nothing capture */}
        <div className="space-y-5">
          <div className={`rounded-2xl border-l-4 border-l-ma-navy p-5 ${isLocked ? 'bg-green-50/30 border-t border-r border-b border-t-green-200 border-r-green-200 border-b-green-200' : 'bg-[#f0f4ff] border-t border-r border-b border-gray-200'}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-ma-navy">Voyant Do-Nothing Capture</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-sm">
                  These are the baseline numbers — the permanent &quot;before&quot; picture for this client.
                  Enter them from Voyant before the session begins.
                </p>
              </div>
              {isLocked && (
                <div className="bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
                  <span>🔒</span> Locked {new Date(voyant.capturedDate).toLocaleDateString('en-GB')}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 uppercase tracking-wide font-semibold">Projected retirement age</label>
                <div className="flex items-center mt-1.5 bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-2">Age</span>
                  <input
                    type="number"
                    value={voyant.retirementAge ?? ''}
                    onChange={e => {
                      const val = e.target.value === '' ? null : parseInt(e.target.value);
                      handleVoyantField('retirementAge', val);
                    }}
                    disabled={isLocked}
                    className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 uppercase tracking-wide font-semibold">Pot at target age (do-nothing)</label>
                <div className="flex items-center mt-1.5 bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-2">£</span>
                  <input {...potInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 uppercase tracking-wide font-semibold">Do-nothing spending capacity (p.a.)</label>
                <div className="flex items-center mt-1.5 bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-2">£</span>
                  <input {...spendingInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                  <span className="text-gray-400 text-xs">/year</span>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 uppercase tracking-wide font-semibold">Lump sum needed to prevent shortfall</label>
                <div className="flex items-center mt-1.5 bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-2">£</span>
                  <input {...lumpSumInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 uppercase tracking-wide font-semibold">Annual additional savings needed</label>
                <div className="flex items-center mt-1.5 bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-2">£</span>
                  <input {...annualSavingsInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                  <span className="text-gray-400 text-xs">/year</span>
                </div>
              </div>
            </div>

            {/* Balance Sheet at Target Age — from Voyant */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-ma-navy uppercase tracking-wide mb-3">Balance Sheet at Target Age (from Voyant)</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Total assets at target age</label>
                  <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                    <span className="text-gray-400 text-sm mr-2">£</span>
                    <input {...assetsInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Total liabilities at target age</label>
                  <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                    <span className="text-gray-400 text-sm mr-2">£</span>
                    <input {...liabilitiesInput} disabled={isLocked} className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`} />
                  </div>
                </div>
                {/* Net worth: auto-calculated but manually overridable */}
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Net worth at target age</label>
                  <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20">
                    <span className="text-gray-400 text-sm mr-2">£</span>
                    <input
                      {...netWorthInput}
                      disabled={isLocked}
                      placeholder={voyant.voyantAssetsAtTarget !== null ? `${(voyant.voyantAssetsAtTarget || 0) - (voyant.voyantLiabilitiesAtTarget || 0)}` : ''}
                      className={`flex-1 bg-transparent text-lg font-bold outline-none ${isLocked ? 'text-gray-500' : 'text-ma-navy'}`}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Auto-calculated from assets minus liabilities — override if needed</p>
                </div>
              </div>
            </div>

            {!isLocked && (
              <div className="mt-6">
                <p className="text-xs text-amber-600 mb-3">
                  These are locked once entered — the permanent &quot;before&quot; picture for this client.
                </p>
                <button
                  onClick={handleLock}
                  disabled={voyant.retirementAge === null || voyant.potAtRetirement === null}
                  className={`w-full py-3 rounded-xl text-sm font-semibold transition-all ${
                    voyant.retirementAge !== null && voyant.potAtRetirement !== null
                      ? 'bg-ma-navy text-white hover:bg-ma-navy-light'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Lock Voyant Capture
                </button>
              </div>
            )}

            {isLocked && voyant.potAtRetirement !== null && (
              <div className="mt-5 bg-white rounded-xl border border-green-200 p-4">
                <div className="text-xs text-gray-400 mb-1">Voyant do-nothing projection (age {voyant.retirementAge})</div>
                <div className="text-xl font-bold text-ma-navy">
                  {fmt(voyant.potAtRetirement)}
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  This is Voyant's projected retirement age — not your client's target age.
                </p>
              </div>
            )}
          </div>

          {/* Quick client info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">Client Info</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Client name</label>
                <input
                  type="text"
                  value={data.clientName}
                  onChange={e => onUpdate({ clientName: e.target.value })}
                  className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Client email</label>
                <input
                  type="email"
                  value={data.clientEmail}
                  onChange={e => onUpdate({ clientEmail: e.target.value })}
                  className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Business name</label>
                <input
                  type="text"
                  value={data.businessName}
                  onChange={e => onUpdate({ businessName: e.target.value })}
                  className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="Business name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Client age</label>
                  <input
                    type="number"
                    min="18"
                    max="80"
                    value={data.clientAge || ''}
                    onChange={e => onUpdate({ clientAge: parseInt(e.target.value) || 40 })}
                    className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">
                    <input
                      type="checkbox"
                      checked={data.isCouple}
                      onChange={e => onUpdate({ isCouple: e.target.checked })}
                      className="mr-2 accent-ma-navy"
                    />
                    Couple?
                  </label>
                  {data.isCouple && (
                    <input
                      type="number"
                      min="18"
                      max="80"
                      value={data.partnerAge || ''}
                      onChange={e => onUpdate({ partnerAge: parseInt(e.target.value) || 0 })}
                      placeholder="e.g. 60"
                      className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
